import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Calendar from '../components/Calendar';
import { Users, Clock, MapPin, Home, Package, Building, Truck, ChevronDown, ChevronUp, Phone, X } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingInfo, setBookingInfo] = useState(null);

  // Check if coming from successful booking
  useEffect(() => {
    if (location.state?.bookingSuccess) {
      setBookingInfo({
        bookingId: location.state.bookingId,
        customerEmail: location.state.customerEmail
      });
      setShowSuccessModal(true);
      
      // Clear the state to prevent showing modal on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Handle hash navigation for "Book It NOW" section
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#book-now') {
      setTimeout(() => {
        const element = document.getElementById('book-now');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);

  const services = [
    { name: 'Small Moves', description: 'Apartment and studio relocations' },
    { name: 'Furniture Moving', description: 'Individual furniture pieces and items' },
    { name: 'Local Moving', description: 'Same-city and neighborhood moves' },
    { name: 'In-State Moving', description: 'Long-distance moves within state' },
    { name: 'Packing Services', description: 'Professional packing and materials' },
    { name: 'Commercial Moving', description: 'Office and business relocations' }
  ];

  const handleDateSelect = (date, price) => {
    setSelectedDate(date);
    setSelectedPrice(price);
  };

  // Auto-navigate after date selection
  useEffect(() => {
    if (selectedDate && selectedPrice) {
      const timer = setTimeout(() => {
        navigate('/size-selection', { 
          state: { 
            selectedDate: selectedDate.toDateString(),
            hourlyRate: selectedPrice
          }
        });
      }, 1500); // Give user time to see the selection

      return () => clearTimeout(timer);
    }
  }, [selectedDate, selectedPrice, navigate]);

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8F7F4'}}>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative animate-fade-in">
            {/* Close button */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Success icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>

            {/* Success message */}
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-3">
              Thank You!
            </h2>
            <p className="text-base sm:text-lg text-gray-700 text-center mb-2">
              Your quote has been submitted successfully!
            </p>
            <p className="text-sm text-gray-600 text-center mb-6">
              Someone will reach out to you shortly at <span className="font-semibold text-teal-600">{bookingInfo?.customerEmail}</span>
            </p>

            {/* Booking ID */}
            {bookingInfo?.bookingId && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-6">
                <p className="text-xs text-gray-600 text-center">Booking ID</p>
                <p className="text-sm font-mono font-semibold text-teal-600 text-center break-all">
                  {bookingInfo.bookingId}
                </p>
              </div>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-500 font-medium">or</span>
              </div>
            </div>

            {/* Call Now section */}
            <div className="text-center">
              <p className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                Need Immediate Attention?
              </p>
              <a 
                href="tel:7134879097"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 hover:shadow-2xl text-white px-6 py-4 rounded-lg font-bold text-lg sm:text-xl shadow-lg transform hover:scale-110 transition-all duration-300 w-full"
              >
                <Phone className="h-6 w-6" />
                <span>CALL NOW</span>
              </a>
              <a 
                href="tel:7134879097"
                className="block mt-3 text-2xl sm:text-3xl font-bold text-red-600 hover:text-red-700 hover:scale-110 transform transition-all duration-300 inline-block"
              >
                (713) 487-9097
              </a>
            </div>

            {/* Close button at bottom */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="mt-6 w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Mobile and Desktop Layout */}
          <div className="space-y-4">
            {/* First Row - Logo and Company Info */}
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-3 cursor-pointer group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_swiftmover/artifacts/7o75lsp7_Emisa%20Movers%20Logo.jpeg" 
                  alt="Emisa Movers Logo"
                  className="h-16 w-16 sm:h-20 sm:w-20 lg:h-28 lg:w-28 flex-shrink-0 transition-all duration-300 transform group-hover:scale-110 group-hover:shadow-2xl group-active:scale-105 rounded-lg"
                />
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 leading-tight">Emisa Movers</h1>
                  <p className="text-xs sm:text-sm text-amber-700 font-medium leading-tight">GETS IT DONE ON TIME</p>
                </div>
              </a>
              
              {/* Desktop Navigation - Hidden on mobile */}
              <nav className="hidden lg:flex gap-6 items-center">
                <a href="#services" className="text-gray-600 hover:text-gray-900 underline hover:-translate-y-0.5 transition-all duration-300 font-semibold transform hover:decoration-2 decoration-gray-600 pb-1">Services</a>
                <a href="#who-we-are" className="text-gray-600 hover:text-gray-900 underline hover:-translate-y-0.5 transition-all duration-300 font-semibold transform hover:decoration-2 decoration-gray-600 pb-1">Who we are</a>
                <a href="#galleria" className="text-gray-600 hover:text-gray-900 underline hover:-translate-y-0.5 transition-all duration-300 font-semibold transform hover:decoration-2 decoration-gray-600 pb-1">Galleria</a>
                <a href="tel:7134879097" className="flex items-center gap-2 bg-red-600 hover:bg-red-700 hover:shadow-2xl hover:scale-110 text-white border-red-600 transition-all duration-300 transform px-4 py-2 rounded-lg font-medium shadow-md">
                  <Phone className="h-4 w-4" />
                  (713) 487-9097
                </a>
                <a href="#book-now" className="bg-teal-600 hover:bg-teal-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg transition-all duration-300 font-medium transform">Book It NOW</a>
              </nav>
            </div>
            
            {/* Second Row - Mobile Navigation */}
            <nav className="lg:hidden">
              <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
                <a href="#services" className="text-gray-600 hover:text-gray-900 underline transition-all duration-300 font-semibold decoration-gray-600 hover:decoration-2 pb-1 text-sm sm:text-base">Services</a>
                <a href="#who-we-are" className="text-gray-600 hover:text-gray-900 underline transition-all duration-300 font-semibold decoration-gray-600 hover:decoration-2 pb-1 text-sm sm:text-base">Who we are</a>
                <a href="#galleria" className="text-gray-600 hover:text-gray-900 underline transition-all duration-300 font-semibold decoration-gray-600 hover:decoration-2 pb-1 text-sm sm:text-base">Galleria</a>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center mt-3">
                <a href="tel:7134879097" className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 hover:shadow-2xl hover:scale-110 text-white px-3 py-2 rounded-lg font-medium shadow-md transition-all duration-300 transform text-sm sm:text-base">
                  <Phone className="h-4 w-4" />
                  (713) 487-9097
                </a>
                <a href="#book-now" className="bg-teal-600 hover:bg-teal-700 hover:shadow-2xl hover:scale-110 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform text-center text-sm sm:text-base">Book It NOW</a>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Hero Section with Calendar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12">
            {/* Left Column - Text Content */}
            <div className="space-y-4 sm:space-y-6">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                  Professional Moving Services
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-4 leading-relaxed">
                  Reliable, efficient, and affordable moving solutions. The services include <span className="underline font-semibold">3-man and 1 truck</span>. Choose your preferred date below!
                </p>
                
                {/* Services Included Bullet Points - Compact */}
                <div className="bg-white border-l-4 border-teal-600 rounded-r-lg p-3 sm:p-4 shadow-sm">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">What's Included:</h3>
                  <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0"></div>
                      <span>3-man professional crew</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0"></div>
                      <span>Fully equipped truck</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0"></div>
                      <span>Furniture pads & plastic wrap</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0"></div>
                      <span>Professional dollies</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-red-600 rounded-full flex-shrink-0"></div>
                      <span className="font-semibold text-red-600">Trip charge is not included</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Column - Calendar (Visible on First Load) */}
            <div className="w-full">
              <div className="text-center mb-3 sm:mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
                  Select Your Moving Date
                </h2>
                <p className="text-xs sm:text-sm text-gray-700">
                  Choose your date and see our rates
                </p>
              </div>
              <Calendar onDateSelect={handleDateSelect} />
              {selectedDate && (
                <div className="mt-3 sm:mt-4 text-center">
                  <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
                      Selected: {selectedDate.toDateString()}
                    </h3>
                    <p className="text-red-600 font-medium text-sm sm:text-base">
                      Rate: ${selectedPrice}/hour
                    </p>
                    <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-700 mt-2">
                      <div className="animate-spin h-4 w-4 border-2 border-teal-600 border-t-transparent rounded-full"></div>
                      <span>Proceeding to next step...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Moving Services
            </h2>
            <p className="text-lg text-gray-700">
              Professional moving solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-white border rounded-lg p-6 mb-4 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 transform cursor-pointer">
                <Home className="h-12 w-12 text-teal-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Local Moving</h3>
              <p className="text-gray-700">
                Same-city and neighborhood moves with careful handling of your belongings
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white border rounded-lg p-6 mb-4 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 transform cursor-pointer">
                <MapPin className="h-12 w-12 text-teal-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">In-State Moving</h3>
              <p className="text-gray-700">
                Reliable long-distance moves within state boundaries
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white border rounded-lg p-6 mb-4 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 transform cursor-pointer">
                <Package className="h-12 w-12 text-teal-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Packing Services</h3>
              <p className="text-gray-700">
                Professional packing with quality materials to protect your items
              </p>
            </div>

            <div className="text-center">
              <div className="bg-white border rounded-lg p-6 mb-4 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 transform cursor-pointer">
                <Building className="h-12 w-12 text-teal-600 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Commercial Moving</h3>
              <p className="text-gray-700">
                Office and business relocations with minimal downtime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section id="who-we-are" className="py-16" style={{backgroundColor: '#F8F7F4'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Who We Are
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Emisa Movers is a professional moving company dedicated to providing reliable, efficient, and stress-free moving services. 
              With years of experience and a team of skilled professionals, we handle every move with care and precision.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-white rounded-lg p-6 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 transform">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Expert Team</h3>
              <p className="text-gray-700">Our trained professionals ensure your belongings are handled with the utmost care and expertise.</p>
            </div>
            
            <div className="text-center bg-white rounded-lg p-6 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 transform">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Reliability</h3>
              <p className="text-gray-700">We pride ourselves on punctuality and keeping our promises. Your move will be completed on time, every time.</p>
            </div>
            
            <div className="text-center bg-white rounded-lg p-6 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 transform">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Local Expertise</h3>
              <p className="text-gray-700">Deep knowledge of the local area ensures efficient routes and seamless moving experiences.</p>
            </div>
          </div>
          
          {/* Truck Image and Google Reviews - Moved from Hero Section */}
          <div className="grid lg:grid-cols-2 gap-12 mt-16">
            {/* Truck Image */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600518464441-9154a4dea21b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxtb3ZpbmclMjB0cnVja3xlbnwwfHx8fDE3NTQ1ODA3MTZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional Moving Truck"
                className="w-full h-64 object-cover rounded-lg shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>

            {/* Google Reviews Section - Placeholder for Direct Google Reviews Integration */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 transform">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://developers.google.com/static/maps/documentation/images/google_g_logo.svg" 
                  alt="Google"
                  className="h-6 w-6"
                />
                <h3 className="text-lg font-semibold text-gray-900">Google Reviews</h3>
              </div>
              
              {/* Placeholder space for Google Reviews - To be populated directly from Google Reviews API */}
              <div className="min-h-[300px] flex flex-col items-center justify-center text-center space-y-4 py-8">
                <div className="text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  <p className="text-sm text-gray-500">Google Reviews will appear here</p>
                  <p className="text-xs text-gray-400 mt-2">Integrated directly from Google Business Profile</p>
                </div>
                
                <div className="mt-4">
                  <a 
                    href="https://www.google.com/search?q=emisa+movers+reviews" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 hover:-translate-y-0.5 hover:shadow-lg text-sm font-medium transition-all duration-300 transform px-4 py-2 rounded-lg hover:bg-teal-50 inline-block border border-teal-600"
                  >
                    Leave us a review on Google →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Galleria Section */}
      <section id="galleria" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Galleria
            </h2>
            <p className="text-lg text-gray-700">
              See our professional trucks and equipment in action
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Image 1 - Truck at apartment */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/vgeu444t_Tezza-7124.jpeg"
                alt="Moving truck at apartment building"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Image 2 - Equipment in truck */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/xgmbnu1v_Tezza-0988.jpeg"
                alt="Professional moving equipment"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Image 3 - Truck in residential area */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/v94ymq5s_Tezza-1233.jpeg"
                alt="Truck in residential area"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Image 4 - Dollies and equipment */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/m0c19xbj_Tezza-1382.jpeg"
                alt="Moving dollies and equipment"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Image 5 - Empty truck interior */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/14z3rjf0_Tezza-5928.jpeg"
                alt="Clean truck interior"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Image 6 - Truck front view */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/rnoicskl_Tezza-7919.jpeg"
                alt="Professional moving truck"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Image 7 - Truck at house */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/biwb13r1_Tezza-8999.jpeg"
                alt="Truck at residential home"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Image 8 - Loading with ramp */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/ku4fihxk_Tezza-3381.jpeg"
                alt="Loading with professional equipment"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            
            {/* Image 9 - Team member loading */}
            <div className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 transform">
              <img
                src="https://customer-assets.emergentagent.com/job_a4df4737-480a-4046-aa89-eb44fd7c8b60/artifacts/daaeaxlt_Tezza-5441.jpeg"
                alt="Professional mover at work"
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Professional Moving Team
            </h2>
            <p className="text-lg text-gray-700">
              Experienced professionals ready to handle your move with care
            </p>
          </div>
          
          <div className="relative max-w-2xl mx-auto">
            <img
              src="https://images.pexels.com/photos/7464684/pexels-photo-7464684.jpeg"
              alt="Professional Movers"
              className="w-full h-64 object-cover rounded-lg shadow-xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
          </div>
        </div>
      </section>

      {/* Book It NOW Section */}
      <section id="book-now" className="py-16 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Book It NOW!
            </h2>
            <p className="text-xl text-teal-100">
              Ready to move? Fill out your information and we'll get back to you within 1 hour!
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-2xl p-8">
            <form className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-400"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-400"
                  placeholder="(713) 123-4567"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-400"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <label htmlFor="moveDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Move Date
                </label>
                <input
                  type="date"
                  id="moveDate"
                  name="moveDate"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-400"
                />
              </div>
              
              <div>
                <label htmlFor="fromAddress" className="block text-sm font-medium text-gray-700 mb-2">
                  Moving From (Address)
                </label>
                <input
                  type="text"
                  id="fromAddress"
                  name="fromAddress"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-400"
                  placeholder="Current address or zip code"
                />
              </div>
              
              <div>
                <label htmlFor="toAddress" className="block text-sm font-medium text-gray-700 mb-2">
                  Moving To (Address)
                </label>
                <input
                  type="text"
                  id="toAddress"
                  name="toAddress"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-400"
                  placeholder="Destination address or zip code"
                />
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="propertySize" className="block text-sm font-medium text-gray-700 mb-2">
                  Property Size
                </label>
                <select
                  id="propertySize"
                  name="propertySize"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-400"
                >
                  <option value="">Select property size</option>
                  <option value="studio">Studio/1 Bedroom</option>
                  <option value="1-2br">1-2 Bedrooms</option>
                  <option value="2-3br">2-3 Bedrooms</option>
                  <option value="3-4br">3-4 Bedrooms</option>
                  <option value="4+br">4+ Bedrooms</option>
                  <option value="commercial">Commercial/Office</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-2">
                  Special Requests or Additional Information
                </label>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-400"
                  placeholder="Tell us about any special items, access issues, or specific requirements..."
                ></textarea>
              </div>
              
              <div className="md:col-span-2 text-center">
                <button
                  type="submit"
                  className="bg-red-600 hover:bg-red-700 hover:shadow-xl hover:-translate-y-1 hover:scale-105 text-white px-12 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform"
                >
                  Get My Free Quote NOW!
                </button>
                <p className="text-sm text-gray-600 mt-3">
                  We'll contact you within 1 hour with your personalized quote
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <a href="/" className="flex items-center gap-3 mb-4 cursor-pointer group inline-block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_swiftmover/artifacts/7o75lsp7_Emisa%20Movers%20Logo.jpeg" 
                  alt="Emisa Movers Logo"
                  className="h-24 w-24 transition-all duration-300 transform group-hover:scale-110 group-hover:shadow-2xl group-active:scale-105 rounded-lg"
                />
                <h3 className="text-lg font-semibold">Emisa Movers</h3>
              </a>
              <p className="text-gray-400">
                Professional moving services you can trust.
              </p>
              <p className="text-black font-medium mt-2">
                GETS IT DONE ON TIME
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Local Moving</li>
                <li>In-State Moving</li>
                <li>Packing Services</li>
                <li>Commercial Moving</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-gray-400">
                <p>📞 <a href="tel:7134879097" className="hover:text-teal-400 transition-colors">(713) 487-9097</a></p>
                <p>📧 <a href="mailto:info@emisamovers.com" className="hover:text-teal-400 transition-colors">info@emisamovers.com</a></p>
                <p>📍 Your City, State</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 mt-8 text-center text-gray-400">
            <p>&copy; 2025 Emisa Movers. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;